import {MacroItem} from '../macros/macros.types';
import {
  CategoryTopItemCategoriesList,
  FeedRelatedItemMetadata,
  FeedRelatedItemRelationType,
  FeedRelatedItemWithConditions,
  RelatedItemsSignalsPurpose,
  RelationType,
  RemoteRelatedItemsLogicsForFeed,
} from './relatedItems.types';
import _map from 'lodash/map';
import {
  clientRelatedItemsToRemote,
  remoteRelatedItemsToClient,
  toClientRelatedItemsLogicsForFeed,
  toClientRelateItemsMapping,
} from './relatedItems.dataConverter';
import apiV2Service from '../api/api-v2.service';
import waitForJob from '../jobs/utils/waitForJob';
import {JobExecutionParams} from '../jobs/jobs.types';

const basePath = `RelatedItems`;

async function getRelatedItemsLogicsForFeed({
  feedId,
  relationType,
}: {
  feedId: number;
  relationType?: RelationType;
}) {
  const {data} = await apiV2Service.executePostRequest<RemoteRelatedItemsLogicsForFeed[]>(
    `${basePath}/GetRelatedItemsLogicsForFeed`,
    {},
    {feedId, ...(relationType !== undefined ? {relationType} : {})},
  );

  return _map(data, toClientRelatedItemsLogicsForFeed);
}

async function getRelateItemsMappingColNameDic() {
  const {data: mapping} = await apiV2Service.executeGetRequest<Record<string, number>>(
    `${basePath}/GetRelatedItemsMappingColNameDic`,
  );

  return _map(mapping, (value, key) => toClientRelateItemsMapping({value, key}));
}

async function getCategoryTopItemCategoriesList({logicId}: {logicId: number}) {
  const {data} = await apiV2Service.executePostRequest<CategoryTopItemCategoriesList[]>(
    `${basePath}/GetCategoryTopItemCategoriesList`,
    {},
    {logicId},
  );

  return data;
}

async function addRelatedItemsLogic({
  feedId,
  logicsData,
}: {
  feedId: number;
  logicsData: FeedRelatedItemWithConditions;
}) {
  const {data: logicId} = await apiV2Service.executePostRequest<number>(
    `${basePath}/feed/${feedId}`,
    {},
    {
      ...clientRelatedItemsToRemote(logicsData),
    },
  );

  return logicId;
}

function updateRelatedItemsLogic({
  feedId,
  logicId,
  logicData,
}: {
  feedId: number;
  logicId: number;
  logicData: FeedRelatedItemWithConditions;
}) {
  return apiV2Service.executePutRequest(
    `${basePath}/feed/${feedId}/logic/${logicId}`,
    {},
    {
      ...clientRelatedItemsToRemote(logicData),
    },
  );
}

function updateFeedRelatedItems({
  feedId,
  logicId,
  relationType,
}: {
  feedId: number;
  logicId: number;
  relationType: FeedRelatedItemRelationType;
}) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(`Feeds/${feedId}/updateRelatedItems/${logicId}`)
    .then(({data: {executionId: jobId}}) => {
      const itemType =
        relationType === FeedRelatedItemRelationType.FieldToItems
          ? 'Category Top Items'
          : relationType === FeedRelatedItemRelationType.ProductSet
            ? 'Product Set'
            : 'Top Items';
      const jobName = `Update ${itemType} R${logicId} Items`;

      return waitForJob({jobId, jobName});
    });
}

function deleteRelatedItem({feedId, logicId}: {feedId: number; logicId: number}) {
  return apiV2Service.executeDeleteRequest<void>(`${basePath}/feed/${feedId}/logic/${logicId}`);
}

function removeManualItems(id: number, removedItemIds: string[]) {
  return apiV2Service.executePatchRequest(
    `${basePath}/${id}/manualItemIds/remove`,
    {},
    {
      removedItemIds,
    },
  );
}

function addManualItems(id: number, addedItemIds: string[]) {
  return apiV2Service.executePatchRequest(
    `${basePath}/${id}/manualItemIds/add`,
    {},
    {addedItemIds},
  );
}

function getFeedRelatedItemsMetadata(feedId: number, relationType: FeedRelatedItemRelationType) {
  return apiV2Service
    .executeGetRequest<FeedRelatedItemMetadata[]>(`${basePath}/feed/${feedId}`, {relationType})
    .then(({data}) => data);
}

function getFeedRelatedItemExtendedData(feedId: number, logicId: number) {
  return apiV2Service
    .executeGetRequest<FeedRelatedItemWithConditions>(`${basePath}/feed/${feedId}/logic/${logicId}`)
    .then(({data}) => remoteRelatedItemsToClient(data));
}

function fetchSignals(feedId: number, purpose: RelatedItemsSignalsPurpose) {
  return apiV2Service
    .executeGetRequest<MacroItem[]>(`${basePath}/${feedId}/signals`, {purpose})
    .then(({data}) => data);
}

function fetchSuggestions(feedId: number, signalKey: string, searchPattern: string) {
  return apiV2Service
    .executeGetRequest<string[]>(`${basePath}/${feedId}/suggestions`, {
      signalKey,
      searchPattern,
    })
    .then(({data}) => data);
}

async function searchProductSets(prompt: string): Promise<{
  productSets: RemoteRelatedItemsLogicsForFeed[];
  filtersExplanation: string;
}> {
  const {data} = await apiV2Service.executeGetRequest<{
    productSets: RemoteRelatedItemsLogicsForFeed[];
    filtersExplanation: string;
  }>(`${basePath}/SearchProductSets`, {prompt});

  return data;
}

async function searchTopItemsLists(prompt: string): Promise<{
  topItems: RemoteRelatedItemsLogicsForFeed[];
  filtersExplanation: string;
}> {
  const {data} = await apiV2Service.executeGetRequest<{
    topItems: RemoteRelatedItemsLogicsForFeed[];
    filtersExplanation: string;
  }>(`${basePath}/SearchTopItems`, {prompt});

  return data;
}

async function searchCategoryTopItemsLists(prompt: string): Promise<{
  categoryTopItems: RemoteRelatedItemsLogicsForFeed[];
  filtersExplanation: string;
}> {
  const {data} = await apiV2Service.executeGetRequest<{
    categoryTopItems: RemoteRelatedItemsLogicsForFeed[];
    filtersExplanation: string;
  }>(`${basePath}/SearchCategoryTopItems`, {prompt});

  return data;
}

const relatedItemsApi = {
  getRelatedItemsLogicsForFeed,
  getRelateItemsMappingColNameDic,
  getCategoryTopItemCategoriesList,
  addRelatedItemsLogic,
  updateRelatedItemsLogic,
  updateFeedRelatedItems,
  deleteRelatedItem,
  removeManualItems,
  addManualItems,

  getFeedRelatedItemsMetadata,
  getFeedRelatedItemExtendedData,
  fetchSignals,
  fetchSuggestions,
  searchProductSets,
  searchTopItemsLists,
  searchCategoryTopItemsLists,
};

export default relatedItemsApi;
